import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { buttonClass } from '../../design-system'

export default function Login() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const { showError } = useNotification()

  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email || !senha) {
      showError('Preencha todos os campos')
      return
    }

    setLoading(true)

    try {
      const result = await login(email, senha)

      if (result.sucesso) {
        navigate('/dashboard')
      } else {
        showError(result.mensagem || 'Erro ao fazer login')
      }
    } catch (error) {
      showError('Erro ao conectar com o servidor')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 px-4">
      {/* Soft primary accent backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(60rem_40rem_at_50%_-10%,theme(colors.primary.100),transparent)]"
      />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 text-center">
          <img
            src="/assets/logo.png"
            alt="AutoGestão"
            className="mx-auto mb-4 h-20 w-auto"
          />
          <p className="mt-2 text-sm text-slate-500">CRM para Gestão de Veículos</p>
        </div>

        {/* Form Card */}
        <div className="rounded-2xl border border-slate-200/70 bg-white p-8 shadow-[0_10px_40px_-15px_rgba(15,23,42,0.15)]">
          <h2 className="mb-6 text-xl font-bold tracking-tight text-slate-900">Entrar</h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
                E-mail
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 w-full rounded-xl border border-slate-200 px-4 text-sm transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                placeholder="seu@email.com"
                disabled={loading}
              />
            </div>

            {/* Senha */}
            <div>
              <label htmlFor="senha" className="mb-1.5 block text-sm font-medium text-slate-700">
                Senha
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="senha"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="h-11 w-full rounded-xl border border-slate-200 px-4 pr-12 text-sm transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Esqueceu a senha */}
            <div className="text-right">
              <a
                href="#"
                className="text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                Esqueceu a senha?
              </a>
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading} className={`${buttonClass('primary')} h-11 w-full`}>
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-slate-400">
          &copy; {new Date().getFullYear()} AutoGestão. Todos os direitos reservados.
        </p>
      </div>
    </div>
  )
}
